import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { heroSlides } from '../mock';
import { useEnrolmentModal } from '../context/EnrolmentModalContext';

const Hero = () => {
  const { show } = useEnrolmentModal();
  const [i, setI] = useState(0);
  const total = heroSlides.length;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);

  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % total), 6500);
    return () => clearInterval(t);
  }, [total]);

  const slide = heroSlides[i];

  return (
    <section id="home" ref={heroRef} className="relative min-h-[100vh] md:min-h-[110vh] w-full overflow-hidden bg-[#0f0d0b] text-white">
      <motion.div style={{ scale: bgScale, y: bgY }} className="absolute inset-0">
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.image}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <div className="absolute inset-0 kenburns">
            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>
      </motion.div>

      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
        backgroundSize: '80px 80px',
      }} />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="relative min-h-[100vh] md:min-h-[110vh] max-w-7xl mx-auto px-6 flex flex-col justify-center pt-56 md:pt-64 lg:pt-72 pb-24">
        {/* Permanent bold portal banner */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="absolute top-4 md:top-6 left-4 right-4 pointer-events-none"
        >
          <div className="relative w-full border-y-2 border-[#c8942a] bg-black/55 backdrop-blur-md py-4 md:py-7 overflow-hidden">
            {/* Corner gold ticks */}
            <span className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-[#f0c95a]" />
            <span className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-[#f0c95a]" />
            <span className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-[#f0c95a]" />
            <span className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-[#f0c95a]" />

            <div className="flex items-center justify-center gap-4 md:gap-8">
              <span className="hidden md:block w-2.5 h-2.5 rounded-full bg-[#f0c95a] shadow-[0_0_18px_4px_rgba(240,201,90,0.55)]" />
              <div className="font-serif-display font-black text-white uppercase tracking-[0.14em] md:tracking-[0.22em] text-center leading-[1.02] text-[26px] sm:text-[38px] md:text-[54px] lg:text-[68px] xl:text-[78px]">
                <span className="text-[#f0c95a]">WHITEPLAINS</span>
                <span className="mx-3 md:mx-5 text-[#c8942a]">·</span>
                British School
              </div>
              <span className="hidden md:block w-2.5 h-2.5 rounded-full bg-[#f0c95a] shadow-[0_0_18px_4px_rgba(240,201,90,0.55)]" />
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 }}
              className="flex items-center gap-4 mb-6"
            >
              <span className="w-14 h-px bg-[#c8942a]" />
              <span className="font-serif-display text-[13px] tracking-[0.4em] uppercase text-[#f0c95a]">{slide.tag}</span>
            </motion.div>

            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[1.02] font-black">
              {slide.title.split(' ').map((w, idx) => (
                <motion.span
                  key={`${i}-a-${idx}`}
                  initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{ delay: 0.25 + idx * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  className="inline-block mr-3"
                >
                  {w}
                </motion.span>
              ))}
              <br />
              <span className="italic text-[#f0c95a] font-serif-display">
                {slide.highlight.split(' ').map((w, idx) => (
                  <motion.span
                    key={`${i}-b-${idx}`}
                    initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    transition={{ delay: 0.55 + idx * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="inline-block mr-3"
                  >
                    {w}
                  </motion.span>
                ))}
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-8 max-w-xl text-white/85 text-lg font-light leading-relaxed"
            >
              {slide.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button type="button" onClick={() => show()} className="btn-primary">
                Begin Application <ArrowRight size={16} />
              </button>
              <a href="#about" className="btn-ghost-light">
                <Play size={14} /> Watch School Film
              </a>
            </motion.div>
          </motion.div>
        </AnimatePresence>

        <div className="absolute bottom-14 right-8 hidden md:flex items-center gap-4">
          {heroSlides.map((s, idx) => (
            <button key={idx} onClick={() => setI(idx)} className="group flex flex-col items-start gap-2 w-28">
              <div className="slide-indicator w-full">
                <motion.span
                  initial={{ width: 0 }}
                  animate={{ width: idx === i ? '100%' : idx < i ? '100%' : '0%' }}
                  transition={{ duration: idx === i ? 6.4 : 0.4, ease: 'linear' }}
                  style={{ display: 'block', height: '100%', background: '#fff' }}
                />
              </div>
              <span className={`text-[10px] tracking-[0.3em] uppercase transition-colors ${idx === i ? 'text-white' : 'text-white/50 group-hover:text-white/80'}`}>
                0{idx + 1}
              </span>
            </button>
          ))}
        </div>

        <div className="absolute bottom-14 left-6 hidden lg:flex items-end gap-10">
          {[
            { v: '15+', l: 'Years of Excellence' },
            { v: '98%', l: 'IGCSE Pass Rate' },
            { v: '20+', l: 'Global Partners' },
          ].map((s, k) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + k * 0.15 }}
              className="border-l border-[#c8942a]/70 pl-4"
            >
              <div className="font-serif-display text-3xl font-black text-white">{s.v}</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/70 mt-0.5">{s.l}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute right-6 md:right-10 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3 text-white/70"
      >
        <span className="[writing-mode:vertical-rl] text-[10px] tracking-[0.4em] uppercase">Scroll to explore</span>
        <div className="w-px h-14 bg-white/40 relative overflow-hidden">
          <motion.div
            animate={{ y: [0, 56, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-0 left-0 w-full h-4 bg-white"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
